import React, { useState, useEffect } from 'react';
import { PageLayout } from '@voilajsx/uikit/page';
import { Card, CardContent } from '@voilajsx/uikit/card';
import { Button } from '@voilajsx/uikit/button';
import { useNavigate } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@voilajsx/uikit/select';
import { Users, CreditCard, Download, Shield, Coins, TrendingUp, Activity, Trophy, UserPlus, Plus, Calendar } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  AreaChart, Area, CartesianGrid,
} from 'recharts';
import { Header, SEO } from '../../../shared/components';
import { AdminSidebar } from '../components/AdminSidebar';
import { useAuth } from '../../auth';
import { route } from '../../../shared/utils';
import { config } from '../../auth/config';

const GOLD = '#FFD93D';
const COLORS = ['#FFD93D', '#4CAF50', '#2196F3', '#FF5722', '#9C27B0', '#00BCD4'];

const getDateRange = (period: string): { from: string; to: string } | null => {
  const now = new Date();
  const to = now.toISOString().split('T')[0];
  if (period === 'all') return null;
  if (period === 'this_month') {
    return { from: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`, to };
  }
  if (period === 'last_month') {
    const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    return { from: d.toISOString().split('T')[0], to: end.toISOString().split('T')[0] };
  }
  if (period === 'this_quarter') {
    const qStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
    return { from: qStart.toISOString().split('T')[0], to };
  }
  if (period === 'last_quarter') {
    const qStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 - 3, 1);
    const qEnd = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 0);
    return { from: qStart.toISOString().split('T')[0], to: qEnd.toISOString().split('T')[0] };
  }
  if (period === 'this_year') {
    return { from: `${now.getFullYear()}-01-01`, to };
  }
  if (period === 'last_30') {
    const d = new Date(now); d.setDate(d.getDate() - 30);
    return { from: d.toISOString().split('T')[0], to };
  }
  if (period === 'last_90') {
    const d = new Date(now); d.setDate(d.getDate() - 90);
    return { from: d.toISOString().split('T')[0], to };
  }
  return null;
};

const AdminDashboard: React.FC = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const [datePeriod, setDatePeriod] = useState('all');
  const [metrics, setMetrics] = useState({
    totalUsers: 0,
    totalDownloads: 0,
    creditsInCirculation: 0,
    totalGranted: 0,
    totalConsumed: 0,
    totalRevenue: 0,
    downloadsByService: [] as { serviceCode: string; count: number }[],
  });
  const [topDownloaders, setTopDownloaders] = useState<{ name: string; downloads: number; creditsSpent: number }[]>([]);
  const [topConsumers, setTopConsumers] = useState<{ name: string; creditsSpent: number }[]>([]);
  const [monthlyData, setMonthlyData] = useState<{ month: string; downloads: number; creditsUsed: number }[]>([]);
  const [, setIsLoading] = useState(false);

  const isAdminSystem = user?.role === 'admin' && user?.level === 'system';
  const isModeratorManage = user?.role === 'moderator' && user?.level === 'manage';
  const hasAccess = isAdminSystem || isModeratorManage;

  useEffect(() => {
    if (!hasAccess || !token) return;
    const fetchMetrics = async () => {
      setIsLoading(true);
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        [config.auth.headers.frontendKey]: config.auth.headers.frontendKeyValue,
        [config.auth.headers.auth]: `Bearer ${token}`,
      };
      try {
        const [creditRes, downloadRes, userRes, topDlRes, topConsRes] = await Promise.allSettled([
          fetch(`${config.api.baseUrl}/api/credit/admin/summary`, { headers }),
          fetch(`${config.api.baseUrl}/api/download/admin/stats`, { headers }),
          fetch(`${config.api.baseUrl}/api/user/admin/users`, { headers }),
          fetch(`${config.api.baseUrl}/api/download/admin/top-users?limit=5`, { headers }),
          fetch(`${config.api.baseUrl}/api/credit/admin/top-consumers?limit=5`, { headers }),
        ]);

        let creditsInCirculation = 0, totalGranted = 0, totalConsumed = 0, totalRevenue = 0;
        if (creditRes.status === 'fulfilled' && creditRes.value.ok) {
          const d = (await creditRes.value.json()).data || {};
          creditsInCirculation = d.totalBalance || d.creditsInCirculation || 0;
          totalGranted = d.totalGranted || 0;
          totalConsumed = d.totalConsumed || 0;
          totalRevenue = d.totalRevenue || 0;
        }

        let totalDownloads = 0;
        let downloadsByService: { serviceCode: string; count: number }[] = [];
        if (downloadRes.status === 'fulfilled' && downloadRes.value.ok) {
          const d = (await downloadRes.value.json()).data || {};
          totalDownloads = d.totalDownloads || 0;
          downloadsByService = d.downloadsByService || [];
        }

        let totalUsers = 0;
        if (userRes.status === 'fulfilled' && userRes.value.ok) {
          const d = await userRes.value.json();
          totalUsers = d.users?.length || d.total || 0;
        }

        if (topDlRes.status === 'fulfilled' && topDlRes.value.ok) {
          const d = await topDlRes.value.json();
          setTopDownloaders(d.data || []);
        }
        if (topConsRes.status === 'fulfilled' && topConsRes.value.ok) {
          const d = await topConsRes.value.json();
          setTopConsumers(d.data || []);
        }

        setMetrics({ totalUsers, totalDownloads, creditsInCirculation, totalGranted, totalConsumed, totalRevenue, downloadsByService });

        // Fetch monthly trend data from ledger
        try {
          const ledgerRes = await fetch(`${config.api.baseUrl}/api/credit/admin/ledger?pageSize=500`, { headers });
          if (ledgerRes.ok) {
            const ledgerJson = await ledgerRes.json();
            const entries = ledgerJson.data?.entries || [];
            const monthMap: Record<string, { downloads: number; creditsUsed: number }> = {};

            // Build last 6 months
            for (let i = 5; i >= 0; i--) {
              const d = new Date();
              d.setMonth(d.getMonth() - i);
              const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
              monthMap[key] = { downloads: 0, creditsUsed: 0 };
            }

            for (const e of entries) {
              const d = new Date(e.createdAt);
              const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
              if (monthMap[key]) {
                if (e.type === 'download_debit') {
                  monthMap[key].downloads++;
                  monthMap[key].creditsUsed += Math.abs(e.amount);
                }
              }
            }

            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            setMonthlyData(Object.entries(monthMap).map(([key, val]) => ({
              month: monthNames[parseInt(key.split('-')[1]) - 1],
              downloads: val.downloads,
              creditsUsed: val.creditsUsed,
            })));
          }
        } catch {}

      } catch { /* best-effort */ }
      finally { setIsLoading(false); }
    };
    fetchMetrics();
  }, [token, hasAccess]);

  if (!user) return null;

  if (!hasAccess) {
    return (
      <PageLayout size="full">
        <SEO title="Access Denied" description="You don't have permission to access this page" />
        <Header />
        <PageLayout.Content>
          <div className="flex items-center justify-center min-h-96">
            <Card className="w-full max-w-md border-zinc-700">
              <CardContent className="pt-6 text-center">
                <Shield className="h-12 w-12 mx-auto mb-4 text-destructive" />
                <h2 className="text-lg font-semibold mb-2">Access Denied</h2>
                <p className="text-muted-foreground mb-4">You don't have permission to access the admin area.</p>
                <Button asChild><a href={route('/dashboard')}>Back to Dashboard</a></Button>
              </CardContent>
            </Card>
          </div>
        </PageLayout.Content>
      </PageLayout>
    );
  }

  // Chart data
  const creditFlowData = [
    { name: 'Granted', value: metrics.totalGranted, fill: '#4CAF50' },
    { name: 'Consumed', value: metrics.totalConsumed, fill: '#FF5722' },
    { name: 'Remaining', value: metrics.creditsInCirculation, fill: GOLD },
  ];

  const serviceChartData = metrics.downloadsByService.map((d, i) => ({
    name: d.serviceCode.replace('plot_', '').replace('_', ' '),
    downloads: d.count,
    fill: COLORS[i % COLORS.length],
  }));

  const cardStyle = {
    background: '#1e1e1e',
    border: '1px solid #2a2a2a',
    borderRadius: 12,
  };

  return (
    <PageLayout size="full">
      <SEO title="Admin Dashboard" description="Administration dashboard" />
      <Header />
      <PageLayout.Content>
          <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>

            <AdminSidebar />

            {/* Main content */}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 28, paddingBottom: 72 }}>

            <div>

            {/* Page title + filters + quick actions */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 28 }}>
              <div>
                <h1 style={{ fontSize: 26, fontWeight: 700, color: '#fff', marginBottom: 4 }}>Admin Dashboard</h1>
                <p style={{ color: '#666', fontSize: 13 }}>System overview and metrics</p>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                {/* Date filter */}
                <Select value={datePeriod} onValueChange={setDatePeriod}>
                  <SelectTrigger style={{ width: 160, height: 34, fontSize: 12 }}>
                    <Calendar style={{ width: 13, height: 13, marginRight: 4, color: '#666' }} />
                    <SelectValue placeholder="All Time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="last_30">Last 30 Days</SelectItem>
                    <SelectItem value="last_90">Last 90 Days</SelectItem>
                    <SelectItem value="this_month">This Month</SelectItem>
                    <SelectItem value="last_month">Last Month</SelectItem>
                    <SelectItem value="this_quarter">This Quarter</SelectItem>
                    <SelectItem value="last_quarter">Last Quarter</SelectItem>
                    <SelectItem value="this_year">This Year</SelectItem>
                  </SelectContent>
                </Select>

                {/* Quick actions */}
                <button
                  onClick={() => navigate(route('/user/admin/create'))}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px',
                    borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    background: '#1a1a1a', border: '1px solid #333', color: '#ccc',
                    fontFamily: 'inherit', transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#555'; e.currentTarget.style.color = '#fff'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#333'; e.currentTarget.style.color = '#ccc'; }}
                >
                  <UserPlus style={{ width: 13, height: 13 }} /> Add User
                </button>
                <button
                  onClick={() => navigate(route('/admin/credits'))}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px',
                    borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    background: 'rgba(255,217,61,0.1)', border: `1px solid rgba(255,217,61,0.3)`, color: GOLD,
                    fontFamily: 'inherit', transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,217,61,0.15)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,217,61,0.1)'; }}
                >
                  <Plus style={{ width: 13, height: 13 }} /> Add Credits
                </button>
              </div>
            </div>

            {/* Metric Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 20 }}>
              {[
                { label: 'Total Users', value: metrics.totalUsers, icon: Users, color: '#2196F3' },
                { label: 'Total Downloads', value: metrics.totalDownloads, icon: Download, color: '#4CAF50' },
                { label: 'Credits Granted', value: metrics.totalGranted.toLocaleString(), icon: Coins, color: GOLD },
                { label: 'Credits Consumed', value: metrics.totalConsumed.toLocaleString(), icon: TrendingUp, color: '#FF5722' },
              ].map((card) => (
                <div key={card.label} style={{ ...cardStyle, padding: '20px 16px', display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 10, background: `${card.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <card.icon size={20} style={{ color: card.color }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>{card.label}</div>
                    <div style={{ fontSize: 24, fontWeight: 800, color: '#fff' }}>{card.value}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Charts Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 20 }}>

              {/* Credit Flow — horizontal bar */}
              <div style={{ ...cardStyle, padding: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
                  <Activity size={16} style={{ color: GOLD }} />
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>Credit Flow</span>
                </div>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={creditFlowData} layout="vertical" margin={{ left: 8, right: 16 }}>
                    <XAxis type="number" tick={{ fill: '#666', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="name" tick={{ fill: '#aaa', fontSize: 12 }} axisLine={false} tickLine={false} width={70} />
                    <Tooltip
                      contentStyle={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 8, color: '#fff' }}
                      cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                    />
                    <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                      {creditFlowData.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Downloads by Service — pie */}
              <div style={{ ...cardStyle, padding: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
                  <Download size={16} style={{ color: GOLD }} />
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>Downloads by Service</span>
                </div>
                {serviceChartData.length === 0 ? (
                  <div style={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555', fontSize: 13 }}>
                    No downloads yet
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={serviceChartData} dataKey="downloads" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={35} paddingAngle={3}>
                        {serviceChartData.map((_entry, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 8, color: '#fff' }}
                      />
                      <Legend iconType="circle" iconSize={8} formatter={(v) => <span style={{ color: '#aaa', fontSize: 11 }}>{v}</span>} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Monthly Trends */}
            <div style={{ ...cardStyle, padding: 24, marginTop: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
                <Activity size={16} style={{ color: GOLD }} />
                <span style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>Monthly Trends</span>
                <span style={{ fontSize: 11, color: '#555', marginLeft: 'auto' }}>Last 6 months</span>
              </div>
              {monthlyData.length === 0 ? (
                <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555', fontSize: 13 }}>
                  No data yet
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={monthlyData} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e1e1e" vertical={false} />
                    <XAxis dataKey="month" tick={{ fill: '#888', fontSize: 12 }} axisLine={false} tickLine={false} />
                    <YAxis yAxisId="left" tick={{ fill: '#666', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fill: '#666', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 8, color: '#fff' }}
                      formatter={(v: any, name: any) => [name === 'creditsUsed' ? `₹${v}` : v, name === 'creditsUsed' ? 'Credits Used' : 'Downloads']}
                    />
                    <Legend
                      iconType="circle" iconSize={8}
                      formatter={(v) => <span style={{ color: '#aaa', fontSize: 11 }}>{v === 'downloads' ? 'Downloads' : 'Credits Used'}</span>}
                    />
                    <Bar yAxisId="left" dataKey="downloads" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={24} />
                    <Bar yAxisId="right" dataKey="creditsUsed" fill="#22c55e" radius={[4, 4, 0, 0]} barSize={24} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Top Users Charts */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 20 }}>

              {/* Top Users by Downloads */}
              <div style={{ ...cardStyle, padding: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
                  <Trophy size={16} style={{ color: GOLD }} />
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>Top Users by Downloads</span>
                </div>
                {topDownloaders.length === 0 ? (
                  <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555', fontSize: 13 }}>
                    No download data yet
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={topDownloaders.slice(0, 5)} layout="vertical" margin={{ left: 0, right: 24 }}>
                      <XAxis type="number" tick={{ fill: '#666', fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis
                        type="category" dataKey="name" width={120}
                        tick={{ fill: '#ccc', fontSize: 12, fontWeight: 500 }} axisLine={false} tickLine={false}
                        tickFormatter={(v: string) => v.split(' ')[0]}
                      />
                      <Tooltip
                        contentStyle={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 8, color: '#fff' }}
                        cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                        formatter={(v: any, _: any, props: any) => [v, 'Downloads']}
                        labelFormatter={(label: any) => String(label)}
                      />
                      <Bar dataKey="downloads" radius={[0, 6, 6, 0]} fill={GOLD} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Top Users by Credits Consumed */}
              <div style={{ ...cardStyle, padding: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
                  <TrendingUp size={16} style={{ color: '#FF5722' }} />
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>Top Users by Consumption</span>
                </div>
                {topConsumers.length === 0 ? (
                  <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555', fontSize: 13 }}>
                    No consumption data yet
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={topConsumers.slice(0, 5)} layout="vertical" margin={{ left: 0, right: 24 }}>
                      <XAxis type="number" tick={{ fill: '#666', fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis
                        type="category" dataKey="name" width={120}
                        tick={{ fill: '#ccc', fontSize: 12, fontWeight: 500 }} axisLine={false} tickLine={false}
                        tickFormatter={(v: string) => v.split(' ')[0]}
                      />
                      <Tooltip
                        contentStyle={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 8, color: '#fff' }}
                        cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                        formatter={(v: any) => [`₹${v}`, 'Credits Spent']}
                        labelFormatter={(label: any) => String(label)}
                      />
                      <Bar dataKey="creditsSpent" radius={[0, 6, 6, 0]} fill="#FF5722" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            </div>
            </div>
          </div>

      </PageLayout.Content>
    </PageLayout>
  );
};

export default AdminDashboard;
